import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import {
  Listing,
  OWNER_OF_THE_BUSINESS,
  COMMERCIAL_PROPERTY,
  SALE_TYPE,
  REVENUE_MODEL,
  TEASER_VERIFICATION,
  TEASER_TYPE,
  SUCCESSION,
  INCLUDE_VIDEO_IN_TEASER,
} from '../../../../../../../models/Listing';
import utils, { getIsListingOwner } from 'utils';
import api from '../../../../../../../api';
import { useTranslation } from 'react-i18next';
import ListingContext from './Listing.context';
import { useNavigate, useParams } from 'react-router-dom';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PopNotificationsContext from '../../../../../../../providers/PopNotifications/PopNotifications.context';
import FourDotLoader from 'components/FourDotLoader';
import {
  checkIfTeaserIsEnabled,
  mapDeadlineToFormValues,
  MONEY_PROPERTIES,
} from 'router/subrouters/ListingCreationWizard/utils';
import { YesNo } from 'models/Fields';
import { ASKING_PRICE_SELECT, STOCK_AT_VALUE } from 'models/DealRoom';
import { BUSINESS_ROUTE } from 'data/global';
import ListingFieldsRequiredVisibleInfoProvider from 'router/subrouters/ListingCreationWizard/providers/ListingFieldsRequiredVisibleInfo';

export type ListingProviderProps = {
  children?: React.ReactNode;
};

// EDIT LISTING PROVIDER
const ListingProvider: React.FC<ListingProviderProps> = (props) => {
  const { children } = props;
  const { t } = useTranslation();
  const [listing, setListing] = useState<Listing>();
  const [listingIsFetching, setListingIsFetching] = useState(false);
  const { popServerError } = useContext(PopNotificationsContext);
  const { id: listingId } = useParams<{ id: string }>();
  const {
    isSuperAdmin,
    currentUser: {
      firstName,
      lastName,
      email,
      contactNumber,
      linkedinAccount,
      _id: currentUserId,
    },
  } = useContext(CurrentUserContext);

  const defaultDeadlineValue = useMemo(
    () => [{ timeline: t('seekingExpressionsOfInterestBy') }],
    [t],
  );
  const navigate = useNavigate();

  const patchListing = useCallback(
    async (value: Listing) => {
      if (!listing) return;

      const {
        listingContact,
        someoneToReplaceOwner,
        hasCommercialProperty,
        commercialProperty,
        enableTeaser,
        teaserFile,
        embeddedTeaserVideoFile,
        EBITDAMultiple,
        EBITDA,
        accountingPeriod,
        contractedCustomers,
        saleType,
        isEnterprise,
        ...restValues
      } = value;

      const newValues = {
        EBITDA,
        hasCommercialProperty,
        accountingPeriod,
        ...restValues,
        listingContact: listingContact?.linkedinProfile
          ? listingContact
          : { ...listingContact, linkedinProfile: null },
        ...(accountingPeriod ? { EBITDAMultiple: EBITDAMultiple || null } : {}),
        ...(hasCommercialProperty ? { commercialProperty } : {}),
        ...(someoneToReplaceOwner
          ? {
              someoneToReplaceOwner: someoneToReplaceOwner === YesNo.Yes,
            }
          : {}),
        ...(contractedCustomers
          ? {
              contractedCustomers: contractedCustomers === YesNo.Yes,
            }
          : {}),
        ...(saleType && {
          saleType,
        }),
        ...(checkIfTeaserIsEnabled(value) ? { enableTeaser } : {}),
      };

      const {
        data: { listing: listingResponse },
      } = await api.listings.patchListing(listing._id, newValues);

      setListing({ isEnterprise, ...listingResponse });
    },
    [listing],
  );

  useEffect(() => {
    (async () => {
      setListingIsFetching(true);

      try {
        const {
          data: { listing: newListing },
        } = await api.listings.getListing(listingId);

        const canEditListing =
          isSuperAdmin ||
          getIsListingOwner(
            currentUserId,
            newListing?.userId,
            newListing?.isCoOwner,
          );

        if (!canEditListing) navigate(BUSINESS_ROUTE);

        setListing(newListing);
      } catch (e) {
        popServerError(e);
        navigate(BUSINESS_ROUTE, { replace: true });
      } finally {
        setListingIsFetching(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listingFormValues = useMemo(() => {
    if (!listing) return null;

    const {
      deadline,
      listingContact,
      askingPriceSelect,
      stockAtValue,
      highlights,
      products,
      keyAssets,
      adjustments,
      priceDescription,
      ownerOfTheBusiness,
      someoneToReplaceOwner,
      hasCommercialProperty,
      commercialProperty,
      saleType,
      contractedCustomers,
      revenueModel,
      enableTeaser,
      teaserVerification,
      teaserType,
      includeVideoInTeaser,
      succession,
      isEnterprise,
      ...restValues
    } = listing;

    const mappedValues = Object.entries(restValues).reduce(
      (acc, [key, value]) =>
        value || value === 0
          ? {
              ...acc,
              [key]: MONEY_PROPERTIES.includes(key as keyof Listing)
                ? utils.getDotedNumber(value.toString())
                : value,
            }
          : acc,
      {},
    );

    const newDeadline = deadline.length
      ? mapDeadlineToFormValues(deadline)
      : defaultDeadlineValue;

    const newAdjustments = adjustments?.length
      ? adjustments.map(({ netAmount, addBack, grossAmount, name }) => ({
          name,
          netAmount: utils.getDotedNumber(netAmount),
          addBack: utils.getDotedNumber(addBack),
          grossAmount: utils.getDotedNumber(grossAmount),
        }))
      : Array(1).fill(void 0);

    return {
      listingContact: listingContact || {
        firstName,
        lastName,
        email,
        phoneNumber: contactNumber,
        linkedinProfile: linkedinAccount,
      },
      askingPriceSelect: askingPriceSelect || ASKING_PRICE_SELECT.FIXED_PRICE,
      stockAtValue: stockAtValue || STOCK_AT_VALUE.EXCLUDE_STOCK_AT_VALUE,
      includeVideoInTeaser: includeVideoInTeaser || INCLUDE_VIDEO_IN_TEASER.NO,
      hasCommercialProperty: hasCommercialProperty || false,
      commercialProperty: commercialProperty || COMMERCIAL_PROPERTY.SELLING,
      revenueModel: revenueModel || REVENUE_MODEL.AD_HOC,
      ...(checkIfTeaserIsEnabled(listing)
        ? { enableTeaser }
        : { enableTeaser: true }),
      teaserVerification:
        teaserVerification || TEASER_VERIFICATION.WITHOUT_EMAIL_VERIFICATION,
      teaserType: teaserType || TEASER_TYPE.RESOLVE_DIGITAL_TEASER,
      highlights: highlights.length ? highlights : Array(3).fill(void 0),
      products: products.length ? products : Array(2).fill(void 0),
      keyAssets: keyAssets.length ? keyAssets : Array(2).fill(void 0),
      adjustments: newAdjustments,
      deadline: newDeadline,
      priceDescription: priceDescription || 'actual',
      isEnterprise,
      ...(!isEnterprise
        ? {
            contractedCustomers: contractedCustomers ? YesNo.Yes : YesNo.No,
            saleType: saleType || SALE_TYPE.SHARE,
            ownerOfTheBusiness:
              ownerOfTheBusiness || OWNER_OF_THE_BUSINESS.SILENT,
            someoneToReplaceOwner: someoneToReplaceOwner ? YesNo.Yes : YesNo.No,
          }
        : {
            saleType,
            succession: succession || SUCCESSION.GM_IN_PLACE,
            contractedCustomers:
              typeof contractedCustomers === 'boolean'
                ? contractedCustomers
                  ? YesNo.Yes
                  : YesNo.No
                : null,
          }),
      ...mappedValues,
    };
  }, [
    contactNumber,
    defaultDeadlineValue,
    email,
    firstName,
    lastName,
    linkedinAccount,
    listing,
  ]);

  return (
    <ListingContext.Provider
      value={{
        listing,
        listingFormValues,
        listingType: listing?.type,
        patchListing,
        setListing,
      }}
    >
      {listingIsFetching || !listing ? (
        <FourDotLoader />
      ) : (
        <ListingFieldsRequiredVisibleInfoProvider
          listing={listing}
          canChangeRequired={false}
        >
          {children}
        </ListingFieldsRequiredVisibleInfoProvider>
      )}
    </ListingContext.Provider>
  );
};

export default ListingProvider;
